import { Grid, Typography } from '@material-ui/core'
import Breadcrumbs from '@material-ui/core/Breadcrumbs'
import Links from '@material-ui/core/Link'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useLocation } from '@reach/router'
import { graphql, Link, navigate } from 'gatsby'
import { parse } from 'query-string'
import React, { useEffect, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useAppState } from '../appState'
import birdAvatar from '../assets/images/bird-avatar.png'
import chirpyestProImg from '../assets/images/chirpyest-pro.jpg'
import axios from '../axios'
import MiddleSection from '../components/homeSections/homeMiddleSection'
import ItemsPerPageFilter from '../components/ItemsPerPageFilter'
import ProductsSort from '../components/productsSort'
import SEO from '../components/seo'
import Spinner from '../components/spinner'
import Card from '../components/trendingCard'
import { ENDPOINTS, ROUTES } from '../constants'
import Layout from '../layouts/defaultLayout'
import { styles } from '../pagesStyles/categoryListing'
import CHRArrowButton from './../components/arrowButton'
import CHRSectionContainer from './../components/sectionContainer'
import NotFound from './404'
import { shopCategorySeoCopy } from '../seoConfig'
import { getPageWindow, getJoinCardPosition } from '../utils/productPaging'

const PAGE_SIZE = 100

interface LoadPageOptions {
  page: number
  size?: number
  sort?: string
  syncUrl?: boolean
}

const CategoryListingPage = ({ t, categoryName, data }) => {
  const classes = styles()

  const { search } = useLocation()
  const { page: pageParam, size: sizeParam, sort: sortParam } = parse(search)

  const [appState] = useAppState()

  const [category, setCategory] = useState()
  const [loading, setLoading] = useState(true)
  const [pageNumber, setPageNumber] = useState(Number(pageParam) || 1)
  const [pageSize, setPageSize] = useState(Number(sizeParam) || PAGE_SIZE)
  const [sorting, setSorting] = useState(sortParam || 'trending')
  const [productsCount, setProductsCount] = useState(null)
  const [products, setProducts] = useState([])
  const [readMore, setReadMore] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(false)
  const latestRequestId = useRef(0)
  // the category's top products, shown first when sorting by trending; fetched once per category
  const pinnedProducts = useRef<any[] | null>(null)
  const [error, setError] = useState('')

  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true })
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const [isCopied, setIsCopied] = useState(false)
  const [randomPosition, setRandomPosition] = useState(null)

  const MAX_TEXT_LENGTH = isPhone ? 75 : isTablet ? 150 : 234 // to ensure that text lines match the design and show read more button

  const sortingOptions = {
    trending: {
      value: 'sort=trending',
      type: 'trending',
      label: t('memberHome.trending'),
    },
    highestToLowest: {
      value: 'sort=price&dir=desc',
      type: 'highestToLowest',
      label: t('shared.highestToLowest'),
    },
    lowestToHighest: {
      value: 'sort=price&dir=asc',
      type: 'lowestToHighest',
      label: t('shared.lowestToHighest'),
    },
  }

  const updateProducts = (productId: string, shoppingBoardId: number) => {
    setProducts(items => {
      const i = items.findIndex(e => e.product_id === productId)
      return [
        ...items.slice(0, i),
        {
          ...items[i],
          shoppingBoardId,
        },
        ...items.slice(i + 1),
      ]
    })
  }

  const removeProduct = (productId: string) => {
    setProducts(items => {
      return items.filter(e => e.product_id !== productId)
    })
  }

  useEffect(() => {
    ;(async () => {
      try {
        const {
          data: {
            data: { category },
          },
        } = await axios.get(
          ENDPOINTS.categoryByName.replace(':categoryName', categoryName)
        )

        if (category.name) {
          setCategory(category)
          setReadMore(category.description?.length >= MAX_TEXT_LENGTH)
        } else {
          setCategory(null)
        }

        setLoading(false)
      } catch (err) {
        setLoading(false)
        setError(t('messages.somethingWentWrong'))
      }
    })()
  }, [categoryName])

  const getPinnedProducts = async (sortType: string): Promise<any[]> => {
    if (sortType !== 'trending') return []
    if (!pinnedProducts.current) {
      const res = await axios.get(
        ENDPOINTS.categoryTopProducts.replace(':categoryPage', categoryName)
      )
      pinnedProducts.current = res.data.data.map(({ data }: any) => data)
    }
    return pinnedProducts.current
  }

  // Single entry point for every product fetch (first load, paging, sorting, page size).
  const loadPage = async ({
    page,
    size = pageSize,
    sort = sorting,
    syncUrl = true,
  }: LoadPageOptions) => {
    latestRequestId.current += 1
    const requestId = latestRequestId.current
    try {
      setLoadingProducts(true)

      const sortOption = sortingOptions[sort] || sortingOptions.trending
      const pinned = await getPinnedProducts(sort)
      const { pinnedFrom, pinnedTo, offset, limit } = getPageWindow(
        page,
        size,
        pinned.length
      )
      const res = await axios.get(
        `${ENDPOINTS.categoryListing.replace(
          ':categoryName',
          categoryName
        )}?size=${limit}&offset=${offset}&${sortOption.value}`
      )

      // a newer request was made while this one was in flight; its result wins
      if (requestId !== latestRequestId.current) return

      setProducts([
        ...pinned.slice(pinnedFrom, pinnedTo),
        ...res.data.data.results,
      ])
      setProductsCount(res.data.data.total)
      setPageNumber(page)
      setPageSize(size)
      setSorting(sort)
      setLoadingProducts(false)
      if (syncUrl) {
        window.history.pushState(
          '',
          '',
          `?sort=${sort}&page=${page}&size=${size}`
        )
      }
    } catch (err) {
      if (requestId === latestRequestId.current) {
        setError(t('messages.somethingWentWrong'))
      }
    }
  }

  useEffect(() => {
    pinnedProducts.current = null
    loadPage({
      page: Number(pageParam) || 1,
      size: Number(sizeParam) || PAGE_SIZE,
      sort: sortParam || 'trending',
      syncUrl: false,
    })
    setRandomPosition(getJoinCardPosition())
  }, [categoryName])

  const scrollToTop = () => window.scroll({ top: 200, behavior: 'auto' })

  const fetchNextPage = () => {
    scrollToTop()
    loadPage({ page: pageNumber + 1 })
  }

  const fetchPreviousPage = () => {
    scrollToTop()
    loadPage({ page: pageNumber - 1 })
  }

  // changing a filter always starts again from the first page
  const handleSorting = ({ type }: any) => loadPage({ page: 1, sort: type })

  const handlePageSizeChange = (newSize: number) =>
    loadPage({ page: 1, size: newSize })

  const handleCopyLink = () => {
    const joinLink = `${process.env.GATSBY_SITE_URL}/join/${appState.userProfile.userName}-${appState.userId}`
    navigator.clipboard.writeText(joinLink)
    setIsCopied(true)
  }

  if (loading) {
    return <Spinner />
  }

  if (error || !category) {
    return <NotFound error={error} />
  }

  const isReadMore = category.description?.length >= MAX_TEXT_LENGTH
  const catName = category.name.charAt(0).toUpperCase() + category.name.slice(1)

  return (
    <Layout>
      {/*Heading*/}
      <SEO
        {...shopCategorySeoCopy(
          category,
          categoryName,
          (key, name) => t(`seo.category.${key}`, { category: name }) as string
        )}
      />

      <div className={classes.headingWrapper}>
        <div className={classes.headingContainer}>
          <div className={classes.headingTextContainer}>
            <Breadcrumbs aria-label="breadcrumb">
              <Links
                color="inherit"
                rel="preconnect"
                onClick={(
                  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                ) => {
                  event.preventDefault()
                  navigate(ROUTES.home)
                }}
              >
                home
              </Links>
              <Typography color="textPrimary">
                {category.name.toLowerCase()}
              </Typography>
            </Breadcrumbs>
            <div className={classes.headingLogoContainer}>
              <img
                className={classes.headingLogoImage}
                width={100}
                height={100}
                src={category.logo || birdAvatar}
                alt="logo"
              />
              <div className={classes.headingLogoText}>
                {/* <Typography
                  component="h1"
                  className={classes.categoryName}
                  style={{ marginBottom: 0 }}
                  variant="h1"
                >
                  {category.name}
                </Typography> */}
                <span
                  className={classes.categoryName}
                  style={{
                    marginBottom: 0,
                  }}
                >
                  {category.name}
                </span>
                <Typography
                  component="h1"
                  variant="h1"
                  className={classes.seoHeading}
                >
                  {categoryName.includes('_')
                    ? t('seo.category.h1Sub', { category: catName })
                    : t('seo.category.h1', { category: catName })}
                </Typography>
                <Typography
                  component="p"
                  className={classes.categoryDescription}
                  variant="subtitle2"
                >
                  {isReadMore ? (
                    readMore ? (
                      <>
                        {category.description?.slice(0, MAX_TEXT_LENGTH)}...{' '}
                        <a
                          className={classes.readMore}
                          onClick={() => setReadMore(false)}
                        >
                          read more
                        </a>
                      </>
                    ) : (
                      <>
                        {category.description}...{' '}
                        <a
                          className={classes.readMore}
                          onClick={() => setReadMore(true)}
                        >
                          read less
                        </a>
                      </>
                    )
                  ) : (
                    category.description
                  )}
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.actionButtonsContainer}>
            <div className={classes.filtersContainer}>
              <div className={classes.filterButton}>
                <ProductsSort
                  label={t('shared.sortBy')}
                  onFilterChange={handleSorting}
                  selectedFilter={sortingOptions[sorting]}
                  options={Object.values(sortingOptions)}
                />
              </div>
              {/* <div className={classes.filterButton}>
                <ProductsSort
                  label={t('shared.refine')}
                  onFilterChange={() => console.log('filter')}
                  selectedFilter=""
                  options={[]}
                />
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {/*products list*/}
      <div>
        <div className={classes.productsContainer}>
          <div className={classes.countingFilterContainer}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Grid item lg={3} md={4} sm={4} xs={6}>
                <Typography
                  component="p"
                  className={classes.itemsCount}
                  variant="body1"
                >
                  {productsCount}
                  {productsCount >= 10000 ? '+' : ''} {t('shared.items')}
                </Typography>
              </Grid>
              <Grid
                item
                lg={3}
                md={4}
                sm={4}
                xs={6}
                style={{
                  backgroundColor: '#FAFBFD',
                  padding: 0,
                }}
              >
                <ItemsPerPageFilter
                  onFilterChange={handlePageSizeChange}
                  selectedFilter={pageSize}
                />
              </Grid>
            </Grid>
          </div>
          <Grid
            container
            alignItems="stretch"
            spacing={2}
            style={{ minHeight: '5vh' }}
          >
            {loadingProducts ? (
              <Spinner />
            ) : (
              products.map((product, index) => {
                if (index === randomPosition && pageNumber === 1) {
                  return (
                    <>
                      <Grid item lg={6} md={8} sm={12} xs={12}>
                        <div className={classes.joinCard}>
                          <Typography variant="h4" style={{ letterSpacing: 0 }}>
                            join the chirpyest community, share your favorites
                            with friends, earn $ when they buy.
                          </Typography>
                          <Grid container justifyContent="center">
                            {!appState.userId ? (
                              <Link
                                className={classes.joinButton}
                                target="_blank"
                                to={ROUTES.join}
                              >
                                <Typography variant="button">
                                  Join us
                                </Typography>
                              </Link>
                            ) : (
                              <div
                                className={classes.joinButton}
                                onClick={handleCopyLink}
                              >
                                <Typography variant="button">
                                  {isCopied ? 'copied' : 'copy join link'}
                                </Typography>
                              </div>
                            )}
                          </Grid>
                        </div>
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        md={4}
                        sm={4}
                        xs={6}
                        key={product.product_id}
                      >
                        <Card
                          updateProducts={updateProducts}
                          onRemove={removeProduct}
                          userId={appState.userId}
                          productInfo={product}
                          shareable
                        />
                      </Grid>
                    </>
                  )
                }
                return (
                  <Grid
                    item
                    lg={3}
                    md={4}
                    sm={4}
                    xs={6}
                    key={product.product_id}
                  >
                    <Card
                      updateProducts={updateProducts}
                      onRemove={removeProduct}
                      userId={appState.userId}
                      productInfo={product}
                      shareable
                    />
                  </Grid>
                )
              })
            )}
          </Grid>
          <div className={classes.paginationContainer}>
            <CHRArrowButton
              isLeftArrow
              label={t('shared.previous')}
              onClick={fetchPreviousPage}
              disabled={pageNumber === 1 || !productsCount}
            />
            <Typography variant="subtitle2" component="p">
              {pageNumber}/{Math.ceil(productsCount / pageSize) || 1}
            </Typography>
            <CHRArrowButton
              label={t('shared.next')}
              onClick={fetchNextPage}
              disabled={
                pageNumber === Math.ceil(productsCount / pageSize) ||
                !productsCount
              }
            />
          </div>
        </div>
      </div>
      {/* Pro section */}
      <CHRSectionContainer>
        <section>
          <MiddleSection
            handleArrowClick={() => navigate(ROUTES.chirpyestPro)}
            image={chirpyestProImg}
            buttonLabel={t('memberHome.learnMore')}
            rightChildren={
              <div>
                <Typography variant="h3">{t('memberHome.proTitle')}</Typography>
                <Typography variant="h2">
                  {t('memberHome.proSubtitle')}
                </Typography>
                <Typography variant="subtitle1" component="p">
                  {t('memberHome.proText')}
                </Typography>
              </div>
            }
          />
        </section>
      </CHRSectionContainer>
    </Layout>
  )
}

export default withTranslation()(CategoryListingPage)

export const query = graphql`
  query {
    allImageSharp(
      filter: { fluid: { originalName: { in: ["getting-hang-of-it.png"] } } }
      sort: { order: ASC, fields: fluid___originalName }
    ) {
      edges {
        node {
          id
          fluid(maxHeight: 1024, quality: 100) {
            src
            srcSet
            base64
            aspectRatio
            originalImg
            originalName
            sizes
          }
        }
      }
    }
  }
`
